import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Calendar,
  Clock,
  User,
  Video,
  MapPin,
  Users,
  Mail,
  Phone,
  AlertTriangle,
  CheckCircle,
  Search,
  Code,
  ArrowRight,
  Copy,
  ExternalLink,
  Mic,
  FileText,
  CreditCard,
  ArrowLeft,
  ChevronDown
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '@/lib/api'
import { apiRequest } from '@/config/api'
import CodingProblemsSelector from '@/components/coding-challenges/CodingProblemsSelector'
import DatabaseProblemsSelector from '@/components/database-challenges/DatabaseProblemsSelector'
import VoiceInterviewModal from './VoiceInterviewModal'
import CreditWarning from '@/components/credits/CreditWarning'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'

interface InterviewScheduleModalProps {
  open: boolean
  onClose: () => void
  candidateId?: string
  candidateName?: string
  positionId?: string
  positionData?: {
    id: string
    title: string
    department: string
  }
  // Edit mode props
  isEdit?: boolean
  interviewId?: string
  editData?: any
  variant?: 'modal' | 'page'
}

interface Candidate {
  id: string
  name: string
  email: string
  position: string
  avatar?: string
  phone?: string
}

interface InterviewData {
  candidateId: string
  positionId: string
  title: string
  type: 'video' | 'phone' | 'in-person' | 'voice'
  date: string
  time: string
  duration: number
  interviewer_emails: string[]
  location?: string
  notes?: string
  stage: 'screening' | 'technical' | 'behavioral' | 'final'
  // Coding problems for technical interviews
  selectedCodingProblems: string[]
  // Database problems for technical interviews  
  selectedDatabaseProblems: string[]
  allowedLanguages: string[]
  codingInstructions: string
  // Video camera requirements
  videoCameraRequired: boolean
  recordInterview: boolean
  allowCandidateVideo: boolean
  // AI assistance option
  allowAiAssistance: boolean
  // Draft mode
  isDraft: boolean
  // Technical interview options (enabled by default for technical interviews)
  enableSystemDesign: boolean
  enableUmlDesign: boolean
  enableWhiteboard: boolean
  enableDataModeling: boolean
  // Optional name field
  interviewName: string
}

export default function InterviewScheduleModal({ 
  open, 
  onClose, 
  candidateId,
  candidateName,
  positionId,
  positionData,
  isEdit = false,
  interviewId,
  editData,
  variant = 'modal'
}: InterviewScheduleModalProps) {
  const interviewDraftEnabled = useFeatureFlag('interviewDraft')
  const aiAssistanceEnabled = useFeatureFlag('interviewAIAssistance')
  const videoCameraEnabled = useFeatureFlag('interviewVideoCamera')
  const navigate = useNavigate()
  const isPageVariant = variant === 'page'

  const [formData, setFormData] = useState<InterviewData>({
    candidateId: candidateId || '',
    positionId: positionId || '',
    title: 'interview',
    type: 'video',
    date: '',
    time: '',
    duration: 60,
    interviewer_emails: [],
    location: '',
    notes: '',
    stage: 'technical',
    selectedCodingProblems: [],
    selectedDatabaseProblems: [],
    allowedLanguages: ['javascript', 'python', 'java'],
    codingInstructions: '',
    videoCameraRequired: true, // Default to required for all rounds
    recordInterview: false,
    allowCandidateVideo: true,
    allowAiAssistance: false, // Default to not allowing AI assistance
    isDraft: false,
    // Technical interview options (enabled by default for technical interviews)
    enableSystemDesign: true,
    enableUmlDesign: true,
    enableWhiteboard: true,
    enableDataModeling: true,
    // Optional name field
    interviewName: ''
  })
  
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [positions, setPositions] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [interviewResult, setInterviewResult] = useState<any>(null)
  const [showVoiceModal, setShowVoiceModal] = useState(false)
  const [newInterviewerEmail, setNewInterviewerEmail] = useState('')
  const [userCredits, setUserCredits] = useState<number>(0)
  const [creditError, setCreditError] = useState<string | null>(null)
  const [codingSectionOpen, setCodingSectionOpen] = useState(false)
  const [databaseSectionOpen, setDatabaseSectionOpen] = useState(false)

  const redirectToInterviewDetailIfNeeded = (interviewData?: any) => {
    if (!isPageVariant) return false

    const extractUuid = (data: any) => {
      if (!data) return null
      if (typeof data.uuid === 'string') return data.uuid
      if (typeof data.id === 'string' && data.id.includes('-')) return data.id
      if (typeof data.interview_uuid === 'string') return data.interview_uuid
      return null
    }

    const interviewUuid = extractUuid(interviewData)
    if (interviewUuid) {
      navigate(`/recruiter/interviews/${interviewUuid}`)
    } else {
      navigate('/recruiter/interviews')
    }
    return true
  }

  useEffect(() => {
    if (!aiAssistanceEnabled) {
      setFormData(prev => prev.allowAiAssistance ? { ...prev, allowAiAssistance: false } : prev)
    }
  }, [aiAssistanceEnabled])

  useEffect(() => {
    if (!videoCameraEnabled) {
      setFormData(prev => {
        if (prev.videoCameraRequired === true && prev.recordInterview === false && prev.allowCandidateVideo === true) {
          return prev
        }
        return {
          ...prev,
          videoCameraRequired: true,
          recordInterview: false,
          allowCandidateVideo: true
        }
      })
    }
  }, [videoCameraEnabled])

  useEffect(() => {
    if (!interviewDraftEnabled) {
      setFormData(prev => prev.isDraft ? { ...prev, isDraft: false } : prev)
    }
  }, [interviewDraftEnabled])

  useEffect(() => {
    if (open) {
      loadCandidatesAndPositions()
      setSubmitted(false)
      setErrors({})
    }
  }, [open])

  // Update form data when candidateId or positionId props change
  useEffect(() => {
    if (candidateId || positionId) {
      setFormData(prev => ({
        ...prev,
        candidateId: candidateId || prev.candidateId,
        positionId: positionId || prev.positionId
      }))
    }
  }, [candidateId, positionId])

  // Handle edit mode - populate form with existing interview data
  useEffect(() => {
    if (isEdit && editData && open) {
      console.log('🔄 Populating edit form with data:', editData)
      console.log('📅 Current formData before edit population:', { date: formData.date, time: formData.time })
      
      // Parse scheduled_at into date and time
      let date = ''
      let time = ''
      if (editData.scheduled_at) {
        const scheduledDate = new Date(editData.scheduled_at)
        // Extract date and time consistently using local timezone
        const year = scheduledDate.getFullYear()
        const month = String(scheduledDate.getMonth() + 1).padStart(2, '0')
        const day = String(scheduledDate.getDate()).padStart(2, '0')
        const hours = String(scheduledDate.getHours()).padStart(2, '0')
        const minutes = String(scheduledDate.getMinutes()).padStart(2, '0')
        
        date = `${year}-${month}-${day}`
        time = `${hours}:${minutes}`
        
        console.log('📅 Extracted date from scheduled_at:', { 
          scheduled_at: editData.scheduled_at, 
          date, 
          time,
          fullDate: scheduledDate.toString()
        })
      }
      
      // Extract coding problem IDs - convert numeric IDs to UUIDs
      let codingProblemIds: string[] = []
      if (editData.codingProblems && Array.isArray(editData.codingProblems)) {
        // Get the numeric problem IDs from assigned problems
        const numericProblemIds = editData.codingProblems.map((p: any) => p.problem_id).filter(Boolean)
        console.log('🔍 Edit form found assigned coding problems with numeric IDs:', numericProblemIds)
        
        // We need to fetch available problems and map numeric IDs to UUIDs
        // This will be done in a separate effect after the form is populated
        if (numericProblemIds.length > 0) {
          // Set a flag to indicate we need to load and map problem UUIDs
          setTimeout(async () => {
            try {
              const problemsResponse = await apiClient.getCodingProblems({ limit: 100 })
              if (problemsResponse.success && problemsResponse.data?.problems) {
                const problemsMap = new Map()
                problemsResponse.data.problems.forEach((p: any) => {
                  problemsMap.set(p.id, p.uuid)
                })
                
                const mappedUUIDs = numericProblemIds
                  .map(id => problemsMap.get(id))
                  .filter(Boolean)
                
                console.log('🔍 Mapped coding problem UUIDs:', mappedUUIDs)
                
                setFormData(prev => ({
                  ...prev,
                  selectedCodingProblems: mappedUUIDs
                }))
              }
            } catch (error) {
              console.error('Failed to load coding problems for mapping:', error)
            }
          }, 100)
        }
      }
      
      // Extract database problem IDs - convert numeric IDs to UUIDs
      let databaseProblemIds: string[] = []
      let extractedTimeLimit = 60
      
      console.log('🔍 Edit form editData.databaseProblems:', editData.databaseProblems)
      
      if (editData.databaseProblems && Array.isArray(editData.databaseProblems)) {
        // The API response already includes challenge_uuid, so we can use it directly
        const challengeUUIDs = editData.databaseProblems.map((p: any) => p.challenge_uuid).filter(Boolean)
        console.log('🔍 Edit form found assigned database problems with UUIDs:', challengeUUIDs)
        
        // Extract time limit from first problem if available
        if (editData.databaseProblems.length > 0) {
          const firstProblem = editData.databaseProblems[0]
          if (firstProblem.time_limit_minutes) {
            extractedTimeLimit = firstProblem.time_limit_minutes
          }
        }

        // Set the UUIDs directly since they're already available
        if (challengeUUIDs.length > 0) {
          databaseProblemIds = challengeUUIDs
          console.log('🔍 Setting database problem UUIDs directly:', databaseProblemIds)
        }
      }
      
      // Map interview type to stage
      let stage: 'screening' | 'technical' | 'behavioral' | 'final' | 'culture-fit' = 'screening'
      if (editData.interview_type === 'technical' || editData.stage === 'technical') {
        stage = 'technical'
      } else if (editData.interview_type === 'behavioral' || editData.stage === 'behavioral') {
        stage = 'behavioral'
      } else if (editData.interview_type === 'final' || editData.stage === 'final') {
        stage = 'final'
      } else if (editData.interview_type === 'culture-fit' || editData.stage === 'culture-fit') {
        stage = 'culture-fit'
      }
      
      // Map format to type
      let type: 'video' | 'phone' | 'in-person' | 'voice' = 'video'
      if (editData.format === 'phone') type = 'phone'
      else if (editData.format === 'in_person' || editData.format === 'in-person') type = 'in-person'
      else if (editData.format === 'voice') type = 'voice'
      
      setFormData({
        candidateId: candidateId || '',
        positionId: positionId || '',
        title: editData.title || 'interview',
        type,
        date,
        time,
        duration: editData.duration_minutes || 60,
        interviewer_emails: editData.interviewer_emails || [],
        location: editData.location || '',
        notes: editData.notes || '',
        stage,
        selectedCodingProblems: codingProblemIds,
        selectedDatabaseProblems: databaseProblemIds,
        // timeLimit removed
        allowedLanguages: editData.allowed_languages || ['javascript', 'python', 'java'],
        codingInstructions: editData.instructions || '',
        videoCameraRequired: editData.video_camera_required ?? true,
        recordInterview: editData.record_interview ?? false,
        allowCandidateVideo: editData.allow_candidate_video ?? true,
        allowAiAssistance: editData.allow_ai_assistance ?? false,
        isDraft: editData.is_draft || false,
        enableSystemDesign: editData.enable_system_design ?? true,
        enableUmlDesign: editData.enable_uml_design ?? true,
        enableWhiteboard: editData.enable_whiteboard ?? true,
        enableDataModeling: editData.enable_data_modeling ?? true,
        interviewName: editData.name || ''
      })
      
      console.log('✅ Edit form populated with stage:', stage)
      console.log('📅 Final formData after edit population:', { date, time, stage })
    }
  }, [isEdit, editData, open, candidateId, positionId])

  const loadCandidatesAndPositions = async () => {
    try {
      const [candidatesResponse, positionsResponse] = await Promise.all([
        apiClient.getCandidates({ limit: 50 }),
        apiClient.getPositions({ limit: 50 })
      ])
      
      if (candidatesResponse.success && candidatesResponse.data) {
        // Handle different response structures
        const rawCandidatesData = Array.isArray(candidatesResponse.data)
          ? candidatesResponse.data
          : candidatesResponse.data.candidates || []
        
        // Map the raw data to the expected format
        const candidatesData = rawCandidatesData.map((candidate: any) => ({
          id: candidate.uuid || candidate.id,
          name: `${candidate.firstName || candidate.first_name || ''} ${candidate.lastName || candidate.last_name || ''}`.trim(),
          email: candidate.email || '',
          position: candidate.currentTitle || candidate.current_title || candidate.position || 'No Title',
          avatar: candidate.avatar,
          phone: candidate.phone
        }))
        
        setCandidates(candidatesData as Candidate[])
      } else {
        setCandidates([])
      }
      
      if (positionsResponse.success && positionsResponse.data) {
        // Handle different response structures
        const rawPositionsData = Array.isArray(positionsResponse.data)
          ? positionsResponse.data
          : positionsResponse.data.positions || []
        
        // Map the raw data to the expected format
        const positionsData = rawPositionsData.map((position: any) => ({
          id: position.uuid || position.id,
          title: position.title || position.name || 'Untitled Position',
          department: position.department || position.departmentName || position.department_name || 'General'
        }))
        
        setPositions(positionsData as any[])
      } else {
        setPositions([])
      }
    } catch (error) {
      console.error('Failed to load data:', error)
      // Ensure arrays are initialized even on error
      setCandidates([])
      setPositions([])
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    
    if (!formData.candidateId) newErrors.candidateId = 'Please select a candidate'
    if (!formData.positionId) newErrors.positionId = 'Please select a position'
    if (formData.type === 'in-person' && !formData.location) {
      newErrors.location = 'Location is required for in-person interviews'
    }
    
    // Check credits for new interviews (not edits)
    if (!isEdit && userCredits === 0) {
      newErrors.credits = 'Insufficient credits to create interview. Please redeem a coupon to add credits.'
    }
    
    // Interviewer emails are now optional - interview link can be shared separately
    
    // Technical interview specific validation
    if (formData.stage === 'technical') {
      const codingProblems = formData.selectedCodingProblems || []
      const databaseProblems = formData.selectedDatabaseProblems || []
      
      if (codingProblems.length === 0 && databaseProblems.length === 0) {
        newErrors.selectedCodingProblems = 'Please select at least one coding problem or database question for technical interviews'
        newErrors.selectedDatabaseProblems = 'Please select at least one coding problem or database question for technical interviews'
      }
    }
    
    // Check if date is not in the past
    if (formData.date && formData.time) {
      const selectedDateTime = new Date(`${formData.date}T${formData.time}`)
      if (selectedDateTime < new Date()) {
        newErrors.datetime = 'Interview cannot be scheduled in the past'
      }
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    setLoading(true)
    try {
      // Handle edit mode
      if (isEdit && interviewId) {
        const scheduledAt = formData.date && formData.time 
          ? new Date(`${formData.date}T${formData.time}:00.000Z`).toISOString()
          : undefined

        const payload = {
          title: formData.title.trim(),
          name: formData.interviewName.trim() || null,
          interview_type: formData.stage,
          format: formData.type === 'in-person' ? 'in_person' : formData.type,
          stage: formData.stage,
          scheduled_at: scheduledAt,
          duration_minutes: formData.duration,
          interviewer_emails: formData.interviewer_emails.filter(email => email.trim()),
          location: formData.location?.trim() || null,
          notes: formData.notes?.trim() || null,
          is_draft: formData.isDraft,
          // Technical interview specific fields
          ...(formData.stage === 'technical' && {
            coding_problem_ids: formData.selectedCodingProblems,
            database_problem_ids: formData.selectedDatabaseProblems,
            // time limit removed
            allowed_languages: formData.allowedLanguages,
            instructions: formData.codingInstructions.trim() || null,
            video_camera_required: formData.videoCameraRequired,
            record_interview: formData.recordInterview,
            allow_candidate_video: formData.allowCandidateVideo,
            allow_ai_assistance: formData.allowAiAssistance,
            enable_system_design: formData.enableSystemDesign,
            enable_uml_design: formData.enableUmlDesign,
            enable_whiteboard: formData.enableWhiteboard,
            enable_data_modeling: formData.enableDataModeling
          })
        }

        console.log('🔄 Updating interview with payload:', payload)
        
        const response = await apiClient.updateInterview(interviewId, payload)
        
        if (response.success) {
          console.log('✅ Interview updated successfully')
          setSubmitted(true)
          // Trigger refresh event
          window.dispatchEvent(new CustomEvent('interviewUpdated'))
          // Close modal after short delay
          setTimeout(() => {
            onClose()
          }, 1500)
        } else {
          throw new Error(response.error?.message || 'Failed to update interview')
        }
        return
      }
      // Combine both coding and database problems with null checks
      const codingProblems = formData.selectedCodingProblems || []
      const databaseProblems = formData.selectedDatabaseProblems || []
      const allProblems = [...codingProblems, ...databaseProblems]
      
      if (formData.stage === 'technical' && allProblems.length > 0 && codingProblems.length > 0 && databaseProblems.length === 0) {
        // For technical interviews with CODING problems only, generate interview URL
        const payload = {
          candidateId: formData.candidateId,
          positionId: formData.positionId,
          problemIds: codingProblems,
          // time limit removed
          allowedLanguages: ['javascript', 'python', 'java'], // Default coding languages
          instructions: formData.codingInstructions,
          interviewTitle: formData.title.trim() || 'Technical Interview',
          interviewName: formData.interviewName.trim() || null,
          allowAiAssistance: formData.allowAiAssistance
        }
        
        const response = await apiClient.generateInterviewUrl({
          ...payload,
          interviewName: payload.interviewName || undefined
        })
        
        if (response.success && response.data) {
          setInterviewResult(response.data)
          setSubmitted(true)
        } else {
          throw new Error(response.error || 'Failed to create technical interview')
        }
      } else {
        // Regular interview scheduling - convert frontend format to backend format
        const scheduledAt = formData.date && formData.time 
          ? new Date(`${formData.date}T${formData.time}:00.000Z`).toISOString()
          : undefined

        // Use credit-aware endpoint for new interviews
        const endpoint = isEdit ? '/interviews' : '/interviews/create-with-credit-check'
        const method = isEdit ? 'PUT' : 'POST'
        
        const payload = {
          candidateId: formData.candidateId,
          positionId: formData.positionId,
          interviewTitle: formData.title,
          name: formData.interviewName.trim() || null,
          timeLimit: formData.timeLimit * 60, // Convert minutes to seconds
          instructions: formData.notes || `${formData.stage} interview`,
          interviewer_emails: [],
          // Include additional fields for compatibility
          interview_type: formData.stage,
          format: formData.type,
          stage: formData.stage,
          scheduled_at: scheduledAt,
          duration_minutes: formData.duration,
          is_draft: formData.isDraft,
          // Include technical interview specific fields with separate arrays for coding and database problems
          ...(formData.stage === 'technical' && allProblems.length > 0 && {
            // Send separate arrays for different problem types
            selected_coding_problems: codingProblems.map(String),
            selected_database_problems: databaseProblems.map(String),
            // Keep legacy field for backward compatibility
            problem_ids: allProblems.map(String),
            allowed_languages: ['javascript', 'python', 'java', 'sql'], // Combined languages
            time_limit: formData.timeLimit * 60, // Convert minutes to seconds
            instructions: formData.codingInstructions
          })
        }
        
        const response = isEdit 
          ? await apiClient.scheduleInterview(payload)
          : await apiClient.request(endpoint, {
              method,
              body: JSON.stringify(payload)
            })
        
        if (response.success) {
          // Dispatch event to refresh interviews list
          window.dispatchEvent(new CustomEvent('interviewScheduled'))
          const redirected = redirectToInterviewDetailIfNeeded(response.data)
          if (redirected) {
            resetForm()
            return
          }
          setSubmitted(true)
          // Auto close after success message for regular interviews
          setTimeout(() => {
            onClose()
            resetForm()
          }, 3000)
        } else {
          // Handle credit-specific errors
          if (response.error === 'INSUFFICIENT_CREDITS' || 
              response.data?.error_type === 'INSUFFICIENT_CREDITS' ||
              response.data?.credit_insufficient) {
            setCreditError('Insufficient credits to create interview')
            throw new Error('Insufficient credits to create interview. Please redeem a coupon to add credits.')
          }
          throw new Error(response.error || 'Failed to schedule interview')
        }
      }
      
    } catch (error) {
      console.error('Failed to schedule interview:', error)
      
      // Enhanced error reporting
      let errorMessage = 'Failed to schedule interview. Please try again.'
      
      if ((error as any).response) {
        // HTTP error response
        console.error('Response status:', (error as any).response.status)
        console.error('Response data:', (error as any).response.data)
        errorMessage = `HTTP ${(error as any).response.status}: ${(error as any).response.data?.detail || (error as any).response.data?.error || 'Server error'}`
      } else if (error instanceof Error && error.message) {
        errorMessage = error.message
      }
      
      setErrors({ submit: errorMessage })
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      candidateId: candidateId || '',
      positionId: positionId || '',
      title: 'interview',
      type: 'video',
      date: '',
      time: '',
      duration: 60,
      interviewer_emails: [],
      location: '',
      notes: '',
      stage: 'technical',
      selectedCodingProblems: [],
      selectedDatabaseProblems: [],
      // timeLimit removed
      allowedLanguages: ['javascript', 'python', 'java'],
      codingInstructions: '',
      videoCameraRequired: true,
      recordInterview: false,
      allowCandidateVideo: true,
      allowAiAssistance: false,
      isDraft: false,
      // Technical interview options (enabled by default for technical interviews)
      enableSystemDesign: true,
      enableUmlDesign: true,
      enableWhiteboard: true,
      enableDataModeling: true,
      // Optional name field
      interviewName: ''
    })
    setSubmitted(false)
    setErrors({})
    setInterviewResult(null)
    setNewInterviewerEmail('')
  }

  const selectedCandidate = (candidates || []).find(c => c.id === formData.candidateId)
  const selectedPosition = (positions || []).find(p => p.id === formData.positionId)
  
  // Auto-populate interview name when candidate or position changes
  React.useEffect(() => {
    if (selectedCandidate && selectedPosition && !formData.interviewName.trim()) {
      const autoName = `${formData.stage} interview - ${selectedPosition.title}`
      setFormData(prev => ({ ...prev, interviewName: autoName }))
    }
  }, [selectedCandidate, selectedPosition, formData.stage])

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      // Could add toast notification here
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
    }
  }

  const isTechnicalInterview = formData.stage === 'technical'
  const codingProblemsCount = (formData.selectedCodingProblems || []).length
  const databaseProblemsCount = (formData.selectedDatabaseProblems || []).length
  const bodyPaddingClass = isPageVariant ? 'p-6 md:p-8' : 'p-6'
  const containerClass = isPageVariant
    ? 'bg-white rounded-xl shadow-sm border border-gray-200 w-full max-w-5xl'
    : 'bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto'
  const wrapperClass = isPageVariant
    ? 'relative z-0 flex justify-center w-full py-4'
    : 'fixed inset-0 z-50 flex items-center justify-center p-4'

  return (
    <AnimatePresence>
      {(open || isPageVariant) && (
        <>
          {!isPageVariant && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50"
              onClick={onClose}
            />
          )}
          
          {/* Modal */}
          <div className={wrapperClass}>
            <motion.div
              initial={isPageVariant ? false : { opacity: 0, scale: 0.95, y: 20 }}
              animate={isPageVariant ? undefined : { opacity: 1, scale: 1, y: 0 }}
              exit={isPageVariant ? undefined : { opacity: 0, scale: 0.95, y: 20 }}
              className={containerClass}
              onClick={(e) => {
                if (!isPageVariant) {
                  e.stopPropagation()
                }
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {isEdit ? 'Edit Interview' : 'Schedule Interview'}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {isEdit ? 'Update interview details and settings' : 'Set up an interview with a candidate'}
                    </p>
                  </div>
                </div>
                {isPageVariant ? (
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary text-sm"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Interviews
                  </button>
                ) : (
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                )}
              </div>

              {/* Content */}
              <div className={bodyPaddingClass}>
                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="py-6"
                  >
                    <div className="text-center mb-6">
                      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {interviewResult ? 'Technical Interview Created!' : 
                         isEdit ? 'Interview Updated!' : 'Interview Scheduled!'}
                      </h3>
                      <p className="text-gray-600">
                        {interviewResult 
                          ? 'The technical interview with coding problems has been set up.'
                          : isEdit ? 'The interview has been successfully updated.'
                          : 'The interview has been successfully scheduled.'
                        }
                      </p>
                    </div>

                    {interviewResult ? (
                      /* Technical Interview Result */
                      <div className="space-y-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center mb-3">
                            <Code className="w-5 h-5 text-blue-600 mr-2" />
                            <h4 className="font-medium text-blue-900">Development Challenge Details</h4>
                          </div>
                          <div className="text-sm text-blue-800 space-y-1">
                            <p><strong>Candidate:</strong> {interviewResult.candidate?.name}</p>
                            <p><strong>Position:</strong> {interviewResult.position?.title}</p>
                            <p><strong>Challenge Type:</strong> Full-Stack Application Development</p>
                            <p><strong>Coding Problems:</strong> {interviewResult.problems?.length} problems available</p>
                                                        <p><strong>Programming Languages:</strong> {formData.allowedLanguages.join(', ')}</p>
                            <p><strong>AI Assistance:</strong> Multiple development agents available</p>
                            <p><strong>Environment:</strong> Full IDE with containerization support</p>
                            <p><strong>Expires:</strong> {new Date(interviewResult.expiresAt).toLocaleString()}</p>
                          </div>
                        </div>

                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <div className="flex items-start space-x-3">
                            <ExternalLink className="w-5 h-5 text-amber-600 mt-0.5" />
                            <div className="flex-1">
                              <h4 className="font-medium text-amber-900 mb-2">Development Environment Access</h4>
                              <p className="text-sm text-amber-800 mb-3">
                                Send this URL to the candidate. They'll have access to a comprehensive development environment with AI assistance, file management, and coding tools.
                              </p>
                              <div className="bg-white border rounded-lg p-3 font-mono text-sm text-gray-700 break-all">
                                {interviewResult.interviewUrl}
                              </div>
                              <div className="flex space-x-2 mt-3">
                                <button
                                  onClick={() => copyToClipboard(interviewResult.interviewUrl)}
                                  className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                                >
                                  <Copy className="w-3 h-3" />
                                  <span>Copy URL</span>
                                </button>
                                <button
                                  onClick={() => window.open(interviewResult.interviewUrl, '_blank')}
                                  className="flex items-center space-x-1 px-3 py-1.5 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  <span>Preview Environment</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h4 className="font-medium text-green-900 mb-2">Development Environment Features</h4>
                          <ul className="text-sm text-green-800 space-y-1">
                            <li>• Full IDE with file system management and multi-folder support</li>
                            <li>• AI development assistants with different specialties and costs</li>
                            <li>• Real-time code editing with syntax highlighting</li>
                            <li>• Terminal integration for running commands and tests</li>
                            <li>• Live preview capabilities for web applications</li>
                            <li>• Performance tracking and prompt-based evaluation</li>
                            <li>• Container-ready project submissions</li>
                          </ul>
                        </div>
                        
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-medium text-blue-900 mb-2">Next Steps</h4>
                          <ul className="text-sm text-blue-800 space-y-1">
                            <li>• Send the development environment URL to the candidate via email</li>
                            <li>• Candidate can build complete applications with multiple files and folders</li>
                            <li>• All project files and AI interactions will be automatically saved</li>
                            <li>• Monitor candidate progress and AI usage from the interviews dashboard</li>
                            <li>• Evaluation based on code quality, architecture, and AI prompt efficiency</li>
                          </ul>
                        </div>

                        <div className="flex justify-center space-x-3 pt-4">
                          <button
                            onClick={() => {
                              onClose()
                              resetForm()
                            }}
                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Regular Interview Result */
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="text-sm text-green-800">
                          <p><strong>Candidate:</strong> {selectedCandidate?.name}</p>
                          <p><strong>Position:</strong> {selectedPosition?.title}</p>
                          <p><strong>Date & Time:</strong> {formData.date && formData.time ? new Date(`${formData.date}T${formData.time}`).toLocaleString() : 'TBD'}</p>
                          <p><strong>Type:</strong> {formData.type}</p>
                          <p><strong>Stage:</strong> {formData.stage}</p>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Credit Warning */}
                    {!isEdit && (
                      <CreditWarning 
                        onCreditChange={(credits) => {
                          setUserCredits(credits)
                          setCreditError(credits === 0 ? 'Insufficient credits' : null)
                        }}
                        className="mb-4"
                      />
                    )}
                    
                    {/* Interview Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Interview Title *
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter interview title"
                        required
                      />
                    </div>

                    {/* Candidate Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Candidate *
                      </label>
                      {(!!candidateId || isEdit) ? (
                        /* Prepopulated candidate display */
                        <div className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-900 flex items-center justify-between">
                          <div>
                            <span className="font-medium">
                              {candidateName || selectedCandidate?.name || 'Loading...'}
                            </span>
                            {selectedCandidate && (
                              <span className="text-gray-500 ml-2">- {selectedCandidate.position}</span>
                            )}
                          </div>
                          <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                            {isEdit ? 'Fixed' : 'Prepopulated'}
                          </span>
                        </div>
                      ) : (
                        /* Normal candidate selection */
                        <select
                          value={formData.candidateId}
                          onChange={(e) => setFormData(prev => ({ ...prev, candidateId: e.target.value }))}
                          className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white ${
                            errors.candidateId ? 'border-red-300' : ''
                          }`}
                        >
                          <option value="" className="text-gray-900">Select a candidate</option>
                          {(candidates || []).map(candidate => (
                            <option key={candidate.id} value={candidate.id} className="text-gray-900">
                              {candidate.name} - {candidate.position}
                            </option>
                          ))}
                        </select>
                      )}
                      {errors.candidateId && (
                        <p className="mt-1 text-sm text-red-600">{errors.candidateId}</p>
                      )}
                    </div>

                    {/* Position Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Position *
                      </label>
                      {(!!positionId || isEdit) ? (
                        /* Prepopulated position display */
                        <div className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-900 flex items-center justify-between">
                          <div>
                            <span className="font-medium">
                              {positionData?.title || selectedPosition?.title || 'Loading...'}
                            </span>
                            {(positionData?.department || selectedPosition?.department) && (
                              <span className="text-gray-500 ml-2">- {positionData?.department || selectedPosition?.department}</span>
                            )}
                          </div>
                          <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                            {isEdit ? 'Fixed' : 'Prepopulated'}
                          </span>
                        </div>
                      ) : (
                        /* Normal position selection */
                        <select
                          value={formData.positionId}
                          onChange={(e) => setFormData(prev => ({ ...prev, positionId: e.target.value }))}
                          className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white ${
                            errors.positionId ? 'border-red-300' : ''
                          }`}
                        >
                          <option value="" className="text-gray-900">Select a position</option>
                          {(positions || []).map(position => (
                            <option key={position.id} value={position.id} className="text-gray-900">
                              {position.title} - {position.department}
                            </option>
                          ))}
                        </select>
                      )}
                      {errors.positionId && (
                        <p className="mt-1 text-sm text-red-600">{errors.positionId}</p>
                      )}
                    </div>

                    {/* Interview Stage */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Interview Stage
                      </label>
                      <select
                        value={formData.stage}
                        onChange={(e) => setFormData(prev => ({ ...prev, stage: e.target.value as any }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                      >
                        <option value="screening" className="text-gray-900 bg-white">Screening</option>
                        <option value="technical" className="text-gray-900 bg-white">Technical</option>
                        <option value="behavioral" className="text-gray-900 bg-white">Behavioral</option>
                        <option value="final" className="text-gray-900 bg-white">Final</option>
                      </select>
                    </div>

                    {/* Interview Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Interview Name
                      </label>
                      <input
                        type="text"
                        value={formData.interviewName}
                        onChange={(e) => setFormData(prev => ({ ...prev, interviewName: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Optional custom name for the interview"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Optional field. Auto-populated based on stage and position, but you can customize it.
                      </p>
                    </div>

                    {/* Date and Time */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date
                        </label>
                        <input
                          type="date"
                          value={formData.date}
                          onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                          min={isEdit ? undefined : new Date().toISOString().split('T')[0]}
                          className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 ${
                            errors.date ? 'border-red-300' : ''
                          }`}
                        />
                        {errors.date && (
                          <p className="mt-1 text-sm text-red-600">{errors.date}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Time
                        </label>
                        <input
                          type="time"
                          value={formData.time}
                          onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                          className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 ${
                            errors.time ? 'border-red-300' : ''
                          }`}
                        />
                        {errors.time && (
                          <p className="mt-1 text-sm text-red-600">{errors.time}</p>
                        )}
                      </div>
                    </div>

                    {errors.datetime && (
                      <p className="text-sm text-red-600">{errors.datetime}</p>
                    )}

                    {/* Interview Type and Duration */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Interview Type
                        </label>
                        <select
                          value={formData.type}
                          onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                        >
                          <option value="video" className="text-gray-900 bg-white">Video Call</option>
                          <option value="phone" className="text-gray-900 bg-white">Phone Call</option>
                          <option value="voice" className="text-gray-900 bg-white">Voice Interview (AI)</option>
                          <option value="in-person" className="text-gray-900 bg-white">In-Person</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Duration (minutes)
                        </label>
                        <select
                          value={formData.duration}
                          onChange={(e) => setFormData(prev => ({ ...prev, duration: Number(e.target.value) }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                        >
                          <option value={30} className="text-gray-900">30 minutes</option>
                          <option value={45} className="text-gray-900">45 minutes</option>
                          <option value={60} className="text-gray-900">1 hour</option>
                          <option value={90} className="text-gray-900">1.5 hours</option>
                          <option value={120} className="text-gray-900">2 hours</option>
                        </select>
                      </div>
                    </div>

                    {/* Location (for in-person interviews) */}
                    {formData.type === 'in-person' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Location *
                        </label>
                        <input
                          type="text"
                          value={formData.location}
                          onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                          placeholder="Office address or meeting room"
                          className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.location ? 'border-red-300' : ''
                          }`}
                        />
                        {errors.location && (
                          <p className="mt-1 text-sm text-red-600">{errors.location}</p>
                        )}
                      </div>
                    )}

                    {/* Interviewer Emails */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Interviewer Emails (Optional)
                      </label>
                      <div className="space-y-3">
                        {/* Add email input */}
                        <div className="flex gap-2">
                          <input
                            type="email"
                            value={newInterviewerEmail}
                            onChange={(e) => setNewInterviewerEmail(e.target.value)}
                            placeholder="Enter interviewer email address"
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                if (newInterviewerEmail.trim() && newInterviewerEmail.includes('@')) {
                                  if (!formData.interviewer_emails.includes(newInterviewerEmail.trim())) {
                                    setFormData(prev => ({
                                      ...prev,
                                      interviewer_emails: [...prev.interviewer_emails, newInterviewerEmail.trim()]
                                    }))
                                    setNewInterviewerEmail('')
                                  }
                                }
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (newInterviewerEmail.trim() && newInterviewerEmail.includes('@')) {
                                if (!formData.interviewer_emails.includes(newInterviewerEmail.trim())) {
                                  setFormData(prev => ({
                                    ...prev,
                                    interviewer_emails: [...prev.interviewer_emails, newInterviewerEmail.trim()]
                                  }))
                                  setNewInterviewerEmail('')
                                }
                              }
                            }}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {/* Selected interviewer emails display */}
                        {formData.interviewer_emails.length > 0 && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-sm text-blue-800 font-medium mb-2">Interviewer Emails:</p>
                            <div className="space-y-1">
                              {formData.interviewer_emails.map((email, index) => (
                                <div
                                  key={index}
                                  className="bg-blue-600 text-white text-sm px-3 py-2 rounded-lg flex items-center justify-between"
                                >
                                  <span className="flex items-center space-x-2">
                                    <Mail className="w-3 h-3" />
                                    <span>{email}</span>
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData(prev => ({
                                        ...prev,
                                        interviewer_emails: prev.interviewer_emails.filter((_, i) => i !== index)
                                      }))
                                    }}
                                    className="hover:bg-blue-700 rounded-full p-1"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {errors.interviewer_emails && (
                          <p className="mt-1 text-sm text-red-600">{errors.interviewer_emails}</p>
                        )}
                        
                        <p className="text-xs text-gray-500">
                          Enter valid email addresses for interviewers. Interview link can be shared separately if needed. Press Enter or click the mail icon to add each email.
                        </p>
                      </div>
                    </div>

                    {/* Technical Interview Settings */}
                    {isTechnicalInterview && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="border-t border-gray-200 pt-6 space-y-6"
                      >
                        <div className="flex items-center space-x-2 mb-4">
                          <Code className="w-5 h-5 text-blue-600" />
                          <h3 className="text-lg font-medium text-gray-900">Technical Interview Setup</h3>
                        </div>

                        {/* Coding Problems Section */}
                        <div className="border border-gray-200 rounded-lg mb-6 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setCodingSectionOpen(prev => !prev)}
                            className="w-full flex items-center justify-between p-4 text-left"
                            aria-expanded={codingSectionOpen}
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900">Coding Problems</p>
                              <p className="text-xs text-gray-500">
                                {codingProblemsCount > 0 
                                  ? `${codingProblemsCount} problem${codingProblemsCount === 1 ? '' : 's'} selected`
                                  : 'No coding problems selected yet'}
                              </p>
                            </div>
                            <ChevronDown
                              className={`w-4 h-4 text-gray-500 transition-transform ${codingSectionOpen ? 'transform rotate-180' : ''}`}
                            />
                          </button>
                          {codingSectionOpen && (
                            <div className="px-4 pb-4">
                              <CodingProblemsSelector
                                selectedProblems={formData.selectedCodingProblems || []}
                                onProblemsChange={(problemIds) => 
                                  setFormData(prev => ({ ...prev, selectedCodingProblems: problemIds }))
                                }
                                maxProblems={5}
                              />
                            </div>
                          )}
                          {errors.selectedCodingProblems && (
                            <p className="px-4 pb-4 text-sm text-red-600">{errors.selectedCodingProblems}</p>
                          )}
                        </div>

                        {/* Database Problems Section */}
                        <div className="border border-gray-200 rounded-lg mb-6 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setDatabaseSectionOpen(prev => !prev)}
                            className="w-full flex items-center justify-between p-4 text-left"
                            aria-expanded={databaseSectionOpen}
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900">Database Problems</p>
                              <p className="text-xs text-gray-500">
                                {databaseProblemsCount > 0
                                  ? `${databaseProblemsCount} question${databaseProblemsCount === 1 ? '' : 's'} selected`
                                  : 'No database questions selected yet'}
                              </p>
                            </div>
                            <ChevronDown
                              className={`w-4 h-4 text-gray-500 transition-transform ${databaseSectionOpen ? 'transform rotate-180' : ''}`}
                            />
                          </button>
                          {databaseSectionOpen && (
                            <div className="px-4 pb-4">
                              <DatabaseProblemsSelector
                                selectedProblems={formData.selectedDatabaseProblems || []}
                                onProblemsChange={(problemIds) => 
                                  setFormData(prev => ({ ...prev, selectedDatabaseProblems: problemIds }))
                                }
                                maxProblems={5}
                              />
                            </div>
                          )}
                          {errors.selectedDatabaseProblems && (
                            <p className="px-4 pb-4 text-sm text-red-600">{errors.selectedDatabaseProblems}</p>
                          )}
                        </div>

                        {/* Coding Instructions */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Special Instructions for Candidate
                          </label>
                          <textarea
                            value={formData.codingInstructions}
                            onChange={(e) => setFormData(prev => ({ ...prev, codingInstructions: e.target.value }))}
                            rows={3}
                            placeholder="e.g., Focus on code clarity, explain your approach, consider edge cases..."
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        {/* Technical Interview Components */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Interview Components (Optional)</h4>
                          <p className="text-xs text-gray-500 mb-4">Select the components you want to include in this technical interview. These are enabled by default but can be disabled if not needed.</p>
                          
                          <div className="grid grid-cols-2 gap-4">
                            {/* System Design */}
                            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                              <div>
                                <label className="text-sm font-medium text-gray-700">System Design</label>
                                <p className="text-xs text-gray-500">Architecture and system design questions</p>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.enableSystemDesign}
                                  onChange={(e) => setFormData(prev => ({ ...prev, enableSystemDesign: e.target.checked }))}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                              </label>
                            </div>

                            {/* UML Design */}
                            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                              <div>
                                <label className="text-sm font-medium text-gray-700">UML Design</label>
                                <p className="text-xs text-gray-500">UML diagrams and modeling</p>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.enableUmlDesign}
                                  onChange={(e) => setFormData(prev => ({ ...prev, enableUmlDesign: e.target.checked }))}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                              </label>
                            </div>

                            {/* Whiteboard */}
                            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                              <div>
                                <label className="text-sm font-medium text-gray-700">Whiteboard</label>
                                <p className="text-xs text-gray-500">Freeform drawing and sketching</p>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.enableWhiteboard}
                                  onChange={(e) => setFormData(prev => ({ ...prev, enableWhiteboard: e.target.checked }))}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                              </label>
                            </div>

                            {/* Data Modeling */}
                            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                              <div>
                                <label className="text-sm font-medium text-gray-700">Data Modeling</label>
                                <p className="text-xs text-gray-500">Database design and data structures</p>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.enableDataModeling}
                                  onChange={(e) => setFormData(prev => ({ ...prev, enableDataModeling: e.target.checked }))}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                              </label>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* AI Assistance Option */}
                    {aiAssistanceEnabled && (
                      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-semibold text-purple-800 flex items-center">
                              <Code className="w-4 h-4 mr-2" />
                              AI Assistance
                            </h4>
                            <p className="text-xs text-purple-600 mt-1">
                              Allow candidate to use AI assistance during the interview
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.allowAiAssistance}
                              onChange={(e) => setFormData(prev => ({ ...prev, allowAiAssistance: e.target.checked }))}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                          </label>
                        </div>
                        
                        <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 mt-3">
                          <div className="flex items-start space-x-2">
                            <Code className="w-4 h-4 text-purple-600 mt-0.5" />
                            <div>
                              <p className="text-xs font-medium text-purple-800">
                                {formData.allowAiAssistance ? 'AI Assistance Enabled' : 'AI Assistance Disabled'}
                              </p>
                              <p className="text-xs text-purple-600 mt-1">
                                {formData.allowAiAssistance 
                                  ? 'Candidates can use AI tools and copilots during the interview. This setting will be clearly communicated to the candidate.'
                                  : 'Candidates are restricted from using AI tools during the interview. This helps evaluate their natural coding abilities.'
                                }
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                        placeholder="Additional notes or interview focus areas..."
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Voice Interview Setup */}
                    {formData.type === 'voice' && (
                      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="text-sm font-semibold text-purple-800 flex items-center">
                              <Mic className="w-4 h-4 mr-2" />
                              AI Voice Interview
                            </h4>
                            <p className="text-xs text-purple-600 mt-1">
                              Automated phone screening with AI-powered analysis
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowVoiceModal(true)}
                            className="flex items-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                          >
                            <Mic className="w-4 h-4" />
                            <span>Setup Questions</span>
                          </button>
                        </div>
                        
                        <div className="bg-purple-100 border border-purple-300 rounded-lg p-3">
                          <div className="flex items-start space-x-2">
                            <Phone className="w-4 h-4 text-purple-600 mt-0.5" />
                            <div>
                              <p className="text-xs font-medium text-purple-800">
                                How Voice Interview Works
                              </p>
                              <ul className="text-xs text-purple-700 mt-1 space-y-0.5">
                                <li>• System automatically calls candidate at scheduled time</li>
                                <li>• AI asks customized questions and records responses</li>
                                <li>• Responses are transcribed and analyzed in real-time</li>
                                <li>• Comprehensive summary with ratings generated</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Video Camera Settings */}
                    {videoCameraEnabled && formData.type === 'video' && (
                      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-purple-800 mb-3 flex items-center">
                          <Video className="w-4 h-4 mr-2" />
                          Video Camera Configuration
                        </h4>
                        
                        <div className="space-y-3">
                        {/* Camera Required Toggle */}
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">
                              Video Camera Required
                            </label>
                            <p className="text-xs text-gray-500">
                              Candidates must enable camera for this interview
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.videoCameraRequired}
                              onChange={(e) => setFormData(prev => ({ ...prev, videoCameraRequired: e.target.checked }))}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                          </label>
                        </div>

                        {/* Record Interview Toggle */}
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">
                              Record Interview
                            </label>
                            <p className="text-xs text-gray-500">
                              Save video recording for later review
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.recordInterview}
                              onChange={(e) => setFormData(prev => ({ ...prev, recordInterview: e.target.checked }))}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                          </label>
                        </div>

                        {/* Allow Candidate Video Toggle */}
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">
                              Allow Candidate Video Control
                            </label>
                            <p className="text-xs text-gray-500">
                              Let candidate turn camera on/off during interview
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.allowCandidateVideo}
                              onChange={(e) => setFormData(prev => ({ ...prev, allowCandidateVideo: e.target.checked }))}
                              disabled={!formData.videoCameraRequired}
                              className="sr-only peer disabled:opacity-50"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600 peer-disabled:bg-gray-300"></div>
                          </label>
                        </div>
                        
                        {/* Camera Requirements Notice */}
                        {formData.videoCameraRequired && (
                          <div className="bg-purple-100 border border-purple-300 rounded-lg p-3">
                            <div className="flex items-start space-x-2">
                              <Video className="w-4 h-4 text-purple-600 mt-0.5" />
                              <div>
                                <p className="text-xs font-medium text-purple-800">
                                  Camera Required Notice
                                </p>
                                <p className="text-xs text-purple-600 mt-1">
                                  Candidates will be notified that video camera is mandatory for this interview.
                                  {formData.recordInterview && " Interview will be recorded for review purposes."}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      </div>
                    )}

                    {/* Draft Mode Toggle */}
                    {interviewDraftEnabled && (
                      <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-semibold text-orange-800 flex items-center">
                              <FileText className="w-4 h-4 mr-2" />
                              Save as Draft
                            </h4>
                            <p className="text-xs text-orange-600 mt-1">
                              Save the interview without sending notifications. You can finalize it later.
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.isDraft}
                              onChange={(e) => setFormData(prev => ({ ...prev, isDraft: e.target.checked }))}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                          </label>
                        </div>
                        
                        {formData.isDraft && (
                          <div className="bg-orange-100 border border-orange-300 rounded-lg p-3 mt-3">
                            <div className="flex items-start space-x-2">
                              <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5" />
                              <div>
                                <p className="text-xs font-medium text-orange-800">
                                  Draft Mode Active
                                </p>
                                <ul className="text-xs text-orange-700 mt-1 space-y-0.5">
                                  <li>• No email notifications will be sent to the candidate</li>
                                  <li>• Interview will be saved with "Draft" status</li>
                                  <li>• You can edit and finalize the interview later</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Error Message */}
                    {errors.submit && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                        <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                        <p className="text-sm text-red-800">{errors.submit}</p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading || (!isEdit && userCredits === 0)}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                        <span>
                          {loading 
                            ? (isEdit 
                                ? 'Updating Interview...'
                                : (isTechnicalInterview && ((formData.selectedCodingProblems || []).length > 0 || (formData.selectedDatabaseProblems || []).length > 0)
                                    ? 'Creating Interview...' 
                                    : formData.isDraft ? 'Saving Draft...' : 'Scheduling...'))
                            : (isEdit 
                                ? 'Update Interview'
                                : (isTechnicalInterview && ((formData.selectedCodingProblems || []).length > 0 || (formData.selectedDatabaseProblems || []).length > 0)
                                    ? 'Create Technical Interview' 
                                    : formData.isDraft ? 'Save as Draft' : 'Schedule Interview'))
                          }
                        </span>
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
      
      {/* Voice Interview Modal */}
      {formData.type === 'voice' && selectedCandidate && selectedPosition && (
        <VoiceInterviewModal
          open={showVoiceModal}
          onClose={() => setShowVoiceModal(false)}
          candidateId={formData.candidateId}
          positionId={formData.positionId}
          candidateName={selectedCandidate.name}
          candidatePhone={selectedCandidate.phone || ''}
          positionTitle={selectedPosition.title}
          onInterviewScheduled={() => {
            setShowVoiceModal(false)
            onClose()
            resetForm()
          }}
        />
      )}
    </AnimatePresence>
  )
}
